import type { FunctionComponent, JSX, TargetedEvent } from "preact";
import { useComputed, useSignal } from "@preact/signals";
import {
  calculateRollout,
  REFERENCE_SPEED_KMH,
  type RolloutResult,
} from "./calc";
import Speedometer from "./Speedometer";

export interface TireCalculatorProps {
  widthRef: number;
  aspectRef: number;
  inchesRef: number;
  widthNew: number;
  aspectNew: number;
  inchesNew: number;
  tolerancePercentMin: number;
  tolerancePercentMax: number;
}

const WIDTH_OPTIONS = [
  125, 135, 140, 145, 155, 165, 175, 185, 195, 205, 215, 225, 235, 245, 255, 265, 275, 285, 295,
  305, 315, 325, 335, 345, 355, 375, 385,
] as const;
const ASPECT_OPTIONS = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90] as const;
const INCHES_OPTIONS = [
  12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29,
] as const;

const findClosest = (target: number, options: readonly number[]): number =>
  options.reduce((best, cur) =>
    Math.abs(cur - target) < Math.abs(best - target) ? cur : best,
  );

const formatMm = (value: number): string =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(value) + " mm";

const formatSignedPercent = (value: number): string => {
  const formatted = new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    signDisplay: "exceptZero",
  }).format(value);
  return `${formatted} %`;
};

const formatSignedKmh = (value: number): string => {
  const formatted = new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(value);
  return `${formatted} km/h`;
};

const formatTolerance = (min: number, max: number): string => {
  const fmt = new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  });
  return `${fmt.format(min)} % bis ${fmt.format(max)} %`;
};

interface DimensionSelectProps {
  label: string;
  value: number;
  options: readonly number[];
  onValue: (value: number) => void;
}

const DimensionSelect = ({
  label,
  value,
  options,
  onValue,
}: DimensionSelectProps): JSX.Element => {
  const handleChange = (event: TargetedEvent<HTMLSelectElement>): void => {
    onValue(Number(event.currentTarget.value));
  };
  return (
    <label>
      <span class="ww-label-row">
        <span class="ww-label">{label}</span>
      </span>
      <select class="ww-input" value={String(value)} onChange={handleChange}>
        {options.map((opt) => (
          <option key={opt} value={String(opt)}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
};

const Widget: FunctionComponent<TireCalculatorProps> = (props) => {
  const { tolerancePercentMin, tolerancePercentMax } = props;

  const widthRef = useSignal<number>(findClosest(props.widthRef, WIDTH_OPTIONS));
  const aspectRef = useSignal<number>(findClosest(props.aspectRef, ASPECT_OPTIONS));
  const inchesRef = useSignal<number>(findClosest(props.inchesRef, INCHES_OPTIONS));
  const widthNew = useSignal<number>(findClosest(props.widthNew, WIDTH_OPTIONS));
  const aspectNew = useSignal<number>(findClosest(props.aspectNew, ASPECT_OPTIONS));
  const inchesNew = useSignal<number>(findClosest(props.inchesNew, INCHES_OPTIONS));

  const result = useComputed<RolloutResult>(() =>
    calculateRollout(
      { width: widthRef.value, aspect: aspectRef.value, inches: inchesRef.value },
      { width: widthNew.value, aspect: aspectNew.value, inches: inchesNew.value },
      REFERENCE_SPEED_KMH,
      tolerancePercentMin,
      tolerancePercentMax,
    ),
  );

  return (
    <div class="root">
      <h3 class="title">Reifendimensionsrechner</h3>
      <p class="subtitle">
        Vergleiche zwei Reifengrößen: berechnet Abrollumfang, prozentuale Abweichung und zeigt
        am Tacho, wie viel schneller oder langsamer die Anzeige bei {REFERENCE_SPEED_KMH} km/h
        tatsächlicher Geschwindigkeit ausfällt.
      </p>

      <fieldset class="fieldset">
        <legend class="legend">Reifen 1 (Referenz)</legend>
        <div class="grid">
          <DimensionSelect
            label="Breite (mm)"
            value={widthRef.value}
            options={WIDTH_OPTIONS}
            onValue={(v) => {
              widthRef.value = v;
            }}
          />
          <DimensionSelect
            label="Querschnitt (%)"
            value={aspectRef.value}
            options={ASPECT_OPTIONS}
            onValue={(v) => {
              aspectRef.value = v;
            }}
          />
          <DimensionSelect
            label="Zoll"
            value={inchesRef.value}
            options={INCHES_OPTIONS}
            onValue={(v) => {
              inchesRef.value = v;
            }}
          />
        </div>
      </fieldset>

      <fieldset class="fieldset">
        <legend class="legend">Reifen 2 (neu)</legend>
        <div class="grid">
          <DimensionSelect
            label="Breite (mm)"
            value={widthNew.value}
            options={WIDTH_OPTIONS}
            onValue={(v) => {
              widthNew.value = v;
            }}
          />
          <DimensionSelect
            label="Querschnitt (%)"
            value={aspectNew.value}
            options={ASPECT_OPTIONS}
            onValue={(v) => {
              aspectNew.value = v;
            }}
          />
          <DimensionSelect
            label="Zoll"
            value={inchesNew.value}
            options={INCHES_OPTIONS}
            onValue={(v) => {
              inchesNew.value = v;
            }}
          />
        </div>
      </fieldset>

      <div class="result">
        <div class="compare">
          <article class="compare-card">
            <span class="compare-label">Reifen 1 — Abrollumfang</span>
            <output class="compare-amount">{formatMm(result.value.refCircumferenceMm)}</output>
          </article>
          <article class="compare-card compare-card--accent">
            <span class="compare-label">Reifen 2 — Abrollumfang</span>
            <output class="compare-amount">{formatMm(result.value.newCircumferenceMm)}</output>
          </article>
        </div>

        <div class="deviation">
          <span class="deviation-label">Differenz</span>
          <span class="deviation-value">
            {formatSignedPercent(result.value.deviationPercent)} ·{" "}
            {formatSignedKmh(result.value.speedDeltaKmh)} bei {REFERENCE_SPEED_KMH} km/h
          </span>
        </div>

        <Speedometer
          displayedSpeedKmh={result.value.displayedSpeedKmh}
          referenceSpeedKmh={REFERENCE_SPEED_KMH}
          toleranceMinPercent={tolerancePercentMin}
          toleranceMaxPercent={tolerancePercentMax}
          withinTolerance={result.value.withinTolerance}
        />

        <p class={`status status--${result.value.withinTolerance ? "ok" : "off"}`}>
          {result.value.withinTolerance
            ? "Die Abweichung liegt in der Toleranz."
            : "Die Abweichung liegt außerhalb der Toleranz."}
        </p>

        <p class="footnote">
          Die Toleranz der Anzeige liegt bei{" "}
          {formatTolerance(tolerancePercentMin, tolerancePercentMax)} Abweichung. (ohne Gewähr)*
        </p>
      </div>
    </div>
  );
};

export default Widget;
